import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

/** 24x24 stroke icons. */
const ICONS = {
  signals: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20V14M9.5 20V10M15 20V6" />
      <circle cx="19.5" cy="5" r="1.8" />
    </>
  ),
  market: <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l5-5 4 4 8-8M15 8h5v5" />,
  quote: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path strokeLinecap="round" d="M20.5 20.5l-4.8-4.8" />
    </>
  ),
  manage: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10.5-10.5a2.1 2.1 0 00-3-3L5 17v3z" />
      <path strokeLinecap="round" d="M13.5 8.5l3 3" />
    </>
  ),
  notify: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 10.5v3a1 1 0 001 1h2l5 4v-13l-5 4H5a1 1 0 00-1 1z" />
      <path strokeLinecap="round" d="M16 9.5a3.5 3.5 0 010 5M18.5 7a7 7 0 010 10" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path strokeLinecap="round" d="M3.5 19c.6-3 2.8-4.8 5.5-4.8s4.9 1.8 5.5 4.8" />
      <circle cx="17" cy="9.5" r="2.4" />
      <path strokeLinecap="round" d="M16.2 14.4c2.3.1 3.9 1.6 4.3 4.1" />
    </>
  ),
  earnings: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path strokeLinecap="round" d="M3.5 10h17M8 3v4M16 3v4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.5v5M10.4 13.6c0-.6.7-1.1 1.6-1.1s1.6.5 1.6 1.1-.7 1-1.6 1-1.6.5-1.6 1.1.7 1.1 1.6 1.1 1.6-.5 1.6-1.1" />
    </>
  ),
};

/**
 * Phone-only bottom tab bar (hidden from the `sm` breakpoint up, where the header
 * menu has room): small icon + label for the main pages. Pads itself above the
 * Android gesture / 3-button bar using the inset Capacitor injects as
 * --safe-area-inset-bottom (falls back to the browser's env() value, then 0).
 */
export default function BottomNav() {
  const { pathname } = useLocation();
  const { marketPageEnabled } = useSettings();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ROLE_ADMIN';

  const signals = { key: 'signals', to: '/signals', label: 'Signals', match: (p) => p === '/signals' };
  // Admin: their day-to-day management pages. Everyone else: the main browsing pages.
  const items = isAdmin
    ? [
        signals,
        { key: 'manage', to: '/admin/signals', label: 'Manage', match: (p) => p === '/admin/signals' },
        { key: 'notify', to: '/admin/notifications', label: 'Notify', match: (p) => p === '/admin/notifications' },
        { key: 'users', to: '/admin/users', label: 'Users', match: (p) => p === '/admin/users' },
      ]
    : [
        signals,
        marketPageEnabled && { key: 'market', to: '/market', label: 'Market', match: (p) => p === '/market' || p === '/admin/market' },
        { key: 'quote', to: '/stock-price', label: 'Quote', match: (p) => p === '/stock-price' || p.startsWith('/stock/') },
        { key: 'earnings', to: '/earnings', label: 'Earnings', match: (p) => p === '/earnings' },
      ].filter(Boolean);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur sm:hidden"
      style={{ paddingBottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))' }}
      aria-label="Main"
    >
      <div className="mx-auto flex max-w-md">
        {items.map((it) => {
          const on = it.match(pathname);
          return (
            <Link
              key={it.key}
              to={it.to}
              aria-current={on ? 'page' : undefined}
              className={`relative flex flex-1 flex-col items-center gap-0.5 pb-1.5 pt-2 text-[10px] font-semibold transition ${
                on ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {on && <span className="absolute inset-x-6 top-0 h-0.5 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600" />}
              <svg className="h-[22px] w-[22px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={on ? 2.2 : 1.8} aria-hidden="true">
                {ICONS[it.key]}
              </svg>
              {it.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
